import classNames from 'classnames';
import { useFormik } from 'formik';
import { isEqual, pick } from 'lodash';
import { useParams } from 'react-router-dom';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Button } from 'ui';

interface CompanyCRSFormik {
  crs_status: string;
}

export const crsStatusItems = [
  {
    label: 'Non-Reporting Financial Institution',
    value: 'non-reporting',
  },
  {
    label: 'Other Active Non-Financial Entity',
    value: 'other-active',
  },
  {
    label: 'Passive Non-Financial Entity',
    value: 'passive',
  },
  {
    label: 'Government Entity, International Organisation and Central Bank',
    value: 'government',
  },
  {
    label:
      'A corporation, the stock of which is regularly traded on an established securities market',
    value: 'corporation',
  },
  {
    label: 'Other',
    value: 'other',
  },
];

const initialValues: CompanyCRSFormik = {
  crs_status: crsStatusItems[0].value,
};

export const CompanyCRS = ({ moveForward, allowSkip, entity_type_prefix }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const getInitialValues = () => {
    return {
      crs_status: data?.entity?.[entity_type_prefix]?.crs_status ?? initialValues.crs_status,
    };
  };

  const formik = useFormik<CompanyCRSFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    initialValues: getInitialValues(),
    onSubmit: async values => {
      const init = pick(values, ['crs_status']);
      const entityTypeDetails = data?.entity?.[entity_type_prefix];
      const current = pick(entityTypeDetails, ['crs_status']);
      if (!isEqual(init, current)) {
        await updateEntity({
          ...data?.entity,
          [entity_type_prefix]: {
            ...data?.entity?.[entity_type_prefix],
            ...values,
          },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeCRSStatus = (value: string) => {
    formik.setFieldValue('crs_status', value);
  };

  return (
    <OnboardingWrapper
      title="Select a classification that matches your CRS status:"
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col gap-2">
        {crsStatusItems.map(({ label, value }, i) => (
          <Button
            key={'ButtonOptions_' + i}
            onClick={() => handleChangeCRSStatus(value)}
            variant="secondary"
            className={classNames({
              'border-[#486a7b]': formik.values.crs_status === value,
            })}
            textAlign="left"
          >
            {label}
          </Button>
        ))}
      </div>
    </OnboardingWrapper>
  );
};
